import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";
import { useToast } from "../utils/ToastContext";

export default function OrderDetails({
  order,
  completedRemovalUnlocked = false,
  requestCompletedRemovalAccess,
}) {
  const { showToast } = useToast();
  const [showRemoveItemModal, setShowRemoveItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [pendingItemAfterPin, setPendingItemAfterPin] = useState(null);
  const [removingItem, setRemovingItem] = useState(false);
  const [requestingDriver, setRequestingDriver] = useState(false);
  const [error, setError] = useState("");

  const formatWhatsAppPhone = (phone) => {
    if (!phone) return "";
    return String(phone).replace(/[^\d]/g, "");
  };

  const whatsappPhone = useMemo(
    () => formatWhatsAppPhone(order?.customer_phone || ""),
    [order?.customer_phone],
  );

  const canWhatsApp = Boolean(whatsappPhone);

  const isCompletedOrder =
    String(order?.status || "")
      .trim()
      .toLowerCase() === "completed";

  const isDeliveryOrder =
    String(order?.order_type || "")
      .trim()
      .toLowerCase() === "delivery";

  const [driverRequested, setDriverRequested] = useState(
    Number(order?.driver_requested) === 1,
  );

  const hasDriverBeenRequested =
    Number(order?.driver_requested) === 1 || driverRequested;

  const canRequestDriver =
    isDeliveryOrder &&
    isCompletedOrder &&
    !hasDriverBeenRequested &&
    !requestingDriver;

  const activeItemsCount =
    order.items?.filter(
      (item) => !(Number(item.is_removed) === 1 || item.is_removed === true),
    ).length || 0;

  useEffect(() => {
    if (
      completedRemovalUnlocked &&
      pendingItemAfterPin &&
      !showRemoveItemModal
    ) {
      setSelectedItem(pendingItemAfterPin);
      setShowRemoveItemModal(true);
      setPendingItemAfterPin(null);
    }
  }, [completedRemovalUnlocked, pendingItemAfterPin, showRemoveItemModal]);

  const openRemoveItemFlow = (e, item) => {
    e.stopPropagation();

    const isRemoved =
      Number(item?.is_removed) === 1 || item?.is_removed === true;

    if (isRemoved || activeItemsCount <= 1) return;

    if (isCompletedOrder && !completedRemovalUnlocked) {
      setPendingItemAfterPin(item);
      requestCompletedRemovalAccess?.();
      return;
    }

    setSelectedItem(item);
    setShowRemoveItemModal(true);
  };

  const closeRemoveItemModal = () => {
    if (removingItem) return;
    setShowRemoveItemModal(false);
    setSelectedItem(null);
  };

  const handleRemoveItem = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!selectedItem || removingItem) return;

    if (isCompletedOrder && !completedRemovalUnlocked) {
      showToast(
        "PIN access is required before removing items from a completed order.",
      );
      return;
    }

    if (activeItemsCount <= 1) {
      showToast(
        "The last item cannot be removed. Please cancel the order instead.",
      );
      return;
    }

    const customerName = order?.customer_name || "Customer";
    const orderRef = `ID L-${order.id}`;
    const itemName = selectedItem?.name || "an item";

    const message = `Hi ${customerName}, unfortunately ${itemName} is currently unavailable and has been removed from your order ${orderRef}. The rest of your order will still be processed accordingly. If you would like a replacement, please go ahead and place another order. Sorry for the inconvenience.`;

    if (canWhatsApp) {
      window.open(
        `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`,
        "_blank",
        "noopener,noreferrer",
      );
    }

    setRemovingItem(true);

    try {
      await adminApiRequest({
        script: "remove-order-item",
        data: {
          order_id: order.id,
          order_item_id: selectedItem.id,
        },
      });

      window.location.reload();
    } catch (err) {
      showToast(err.message || "Failed to remove item.");
    } finally {
      setRemovingItem(false);
    }
  };

  const handleRequestDriver = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!canRequestDriver || requestingDriver || hasDriverBeenRequested) {
      return;
    }

    setError("");
    setRequestingDriver(true);

    try {
      const res = await adminApiRequest({
        script: "request-driver",
        data: {
          order_id: order.id,
        },
      });

      if (res?.status === "success") {
        setDriverRequested(true);
      } else {
        setError(res?.message || "Unable to request driver.");
      }
    } catch (err) {
      console.error("Request driver error:", err);
      setError("Failed to request driver.");
    } finally {
      setRequestingDriver(false);
    }
  };

  return (
    <>
      <div className="space-y-2 text-sm text-slate-700">
        <div className="space-y-2">
          {Number(order?.customer_flagged) === 1 && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2">
              <p className="text-rose-700">
                <strong>
                  <i className="fa-solid fa-flag mr-1"></i> Flagged Customer:
                </strong>{" "}
                {order.customer_flag_note || "No note provided."}
              </p>
            </div>
          )}

          {order.customer_phone && (
            <p>
              <strong>Phone No:</strong> {order.customer_phone}
            </p>
          )}

          {isDeliveryOrder && (
            <>
              {order.delivery_address && (
                <p>
                  <strong>Delivery Address:</strong> {order.delivery_address}
                </p>
              )}

              {order.delivery_fee != null && (
                <p>
                  <strong>Delivery Fee:</strong> £
                  {parseFloat(order.delivery_fee).toFixed(2)}
                </p>
              )}

              {order.service_charge != null && order.service_charge > 0 && (
                <p>
                  <strong>Service Charge:</strong> £
                  {parseFloat(order.service_charge).toFixed(2)}
                </p>
              )}

              {isCompletedOrder && (
                <div className="pt-2 flex items-center gap-4 flex-wrap">
                  <button
                    type="button"
                    onClick={handleRequestDriver}
                    disabled={
                      !canRequestDriver ||
                      hasDriverBeenRequested ||
                      requestingDriver
                    }
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white transition ${
                      hasDriverBeenRequested ||
                      requestingDriver ||
                      !canRequestDriver
                        ? "cursor-not-allowed bg-slate-400"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                  >
                    {hasDriverBeenRequested ? (
                      <>
                        <i className="fa-solid fa-check"></i>
                        Driver requested
                      </>
                    ) : requestingDriver ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        Requesting...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-motorcycle"></i>
                        Request Driver
                      </>
                    )}
                  </button>

                  {hasDriverBeenRequested && order?.tracking_url && (
                    <a
                      href={order.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-md font-bold text-blue-600 underline hover:text-blue-800"
                    >
                      Track delivery →
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <hr className="border-slate-200" />

        <div className="space-y-2">
          {order.items?.map((item) => {
            const mainProteinIds = [103, 104, 105, 106, 107, 108];

            const mainProteins =
              item.proteins?.filter((p) =>
                mainProteinIds.includes(Number(p.id)),
              ) || [];

            const extraProteins =
              item.proteins?.filter(
                (p) => !mainProteinIds.includes(Number(p.id)),
              ) || [];

            const optionTotal = [
              ...(item.selectedOptionalOptions || []),
              ...(item.swallowOptions || []),
              ...(item.proteins || []),
            ].reduce((sum, opt) => sum + opt.price * opt.quantity, 0);

            const itemTotal =
              (parseFloat(item.base_price || 0) +
                parseFloat(item.required_option_price || 0) +
                optionTotal) *
              (item.quantity || 1);

            const isRemoved =
              Number(item.is_removed) === 1 || item.is_removed === true;

            const canRemoveThisItem = !isRemoved && activeItemsCount > 1;

            return (
              <div
                key={item.id}
                className={`relative rounded-2xl p-3 ring-1 transition ${
                  isRemoved
                    ? "bg-slate-100/90 opacity-60 ring-slate-300"
                    : "bg-white ring-slate-200"
                }`}
              >
                {isRemoved && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg">
                      <i className="fa-solid fa-ban text-xl"></i>
                    </div>
                  </div>
                )}

                <div className="mb-1 flex items-start justify-between gap-3">
                  <span
                    className={`text-md font-bold ${
                      isRemoved
                        ? "text-slate-400 line-through"
                        : "text-slate-900"
                    }`}
                  >
                    {item.name} (£{parseFloat(item.base_price || 0).toFixed(2)})
                  </span>

                  {!isRemoved &&
                    (canRemoveThisItem ? (
                      <button
                        type="button"
                        onClick={(e) => openRemoveItemFlow(e, item)}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-600 transition hover:bg-red-100 hover:text-red-700"
                        title={
                          isCompletedOrder && !completedRemovalUnlocked
                            ? "PIN required to remove item from a completed order"
                            : "Remove item"
                        }
                      >
                        <i className="fa-solid fa-trash text-sm"></i>
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled
                        className="flex h-9 w-9 cursor-not-allowed items-center justify-center rounded-full bg-slate-100 text-slate-400"
                        title="The last item cannot be removed. Cancel the order instead."
                      >
                        <i className="fa-solid fa-trash text-sm"></i>
                      </button>
                    ))}
                </div>

                {item.required_option_id && (
                  <div className="mb-2">
                    <ul className="list-disc space-y-1 pl-5 text-md">
                      <li className={isRemoved ? "text-slate-400" : ""}>
                        <span className={isRemoved ? "line-through" : ""}>
                          {item.required_option_name}
                        </span>{" "}
                        <span className="text-lg font-bold text-red-500">
                          x {item.quantity}
                        </span>{" "}
                        (£
                        {(
                          parseFloat(item.required_option_price || 0) *
                          item.quantity
                        ).toFixed(2)}
                        )
                      </li>
                    </ul>
                  </div>
                )}

                {(item.selectedOptionalOptions || []).length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Optional:
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {item.selectedOptionalOptions.map((opt, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {opt.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {opt.quantity}
                          </span>{" "}
                          (£{(opt.price * opt.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(item.swallowOptions || []).length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Swallow:
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {item.swallowOptions.map((sw, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {sw.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {sw.quantity}
                          </span>{" "}
                          (£{(sw.price * sw.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {mainProteins.length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Protein(s):
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {mainProteins.map((p, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {p.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {p.quantity}
                          </span>{" "}
                          (£{(p.price * p.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {extraProteins.length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Extra:
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {extraProteins.map((p, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {p.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {p.quantity}
                          </span>{" "}
                          (£{(p.price * p.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3 flex justify-end border-t border-slate-200 pt-3 text-md">
                  <span
                    className={`font-bold ${
                      isRemoved
                        ? "text-slate-400 line-through"
                        : "text-slate-900"
                    }`}
                  >
                    <span className="text-lg font-bold text-green-700 drop-shadow-sm">
                      x {item.quantity}
                    </span>{" "}
                    = £{itemTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <hr className="border-slate-200" />

        {order.loyalty_reward_used === 1 && (
          <p className="text-right text-red-600 font-bold text-lg uppercase tracking-tight">
            Loyalty Reward Applied: -£{parseFloat(order.loyalty_discount || 0).toFixed(2)}
          </p>
        )}

        {order.notes?.trim() && (
          <p className="text-base text-lg font-bold text-red-500">
            <strong>Notes:</strong> <span>{order.notes}</span>
          </p>
        )}
      </div>

      {error && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-sm"
          onClick={() => setError("")}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-rose-200 bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
                <i className="fas fa-exclamation-triangle"></i>
              </div>

              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Error</p>
                <p className="mt-1 text-sm text-slate-600">{error}</p>

                <button
                  onClick={() => setError("")}
                  className="mt-3 rounded-lg bg-rose-600 px-3 py-1 text-sm text-white hover:bg-rose-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRemoveItemModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <Modal
            title="Remove Item"
            onClose={closeRemoveItemModal}
            onSubmit={handleRemoveItem}
            submitButtonText={canWhatsApp ? "Notify and Remove" : "Remove Item"}
            showCancel={true}
            showSubmitButton={true}
            submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
          >
            <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
              <p className="text-sm font-medium text-rose-700">
                Are you sure you want to remove{" "}
                <span className="font-bold">{selectedItem?.name}</span> from
                this order?
              </p>

              {isCompletedOrder && (
                <p className="mt-2 text-xs text-rose-600/80">
                  This completed order has been unlocked with PIN access.
                </p>
              )}

              {canWhatsApp && (
                <p className="mt-2 text-xs text-rose-600/80">
                  The customer will be notified on WhatsApp that this item is
                  unavailable, while the rest of the order will still be
                  processed.
                </p>
              )}

              {activeItemsCount <= 1 && (
                <p className="mt-2 text-xs text-rose-600/80">
                  The last item cannot be removed. Cancel the order instead.
                </p>
              )}
            </div>
          </Modal>
        </div>
      )}
    </>
  );
}
